import { inject, Injectable } from "@angular/core";
import { Action, Selector, State, StateContext, Store } from "@ngxs/store";
import moment from "moment-timezone";
import { catchError, tap } from "rxjs/operators";
import { EMPTY, throwError } from "rxjs";
import { MomentHelper, ResourceApiService } from "@3volutionsag/ngx-rooms-lib";
import { patch } from "@ngxs/store/operators";

import { ConfigurationService } from "../configuration.service";
import { SetServiceUser } from "../../authentication/state/authentication.actions";
import { LoadBookingsOfCurrentDay } from "../../booking/state/booking.actions";
import { DynamicAssetInitializerService } from "../../dynamic-assets/dynamic-asset.initializer";
import { HttpRequestHelperService } from "../../shared/http-request-helper.service";

import {
	FetchDsConfiguration,
	SetDsConfiguration,
	SetRootConfiguration,
	ResetDsConfiguration,
} from "./configuration.actions";

import type { DsConfigurationModel, RequestOptions } from "@3volutionsag/ngx-rooms-lib";
import type { RootConfigurationModel } from "../root-config.model";
import type { NgxsAfterBootstrap, NgxsOnInit } from "@ngxs/store";

export interface ConfigurationStateModel {
	dsConfiguration: DsConfigurationModel | null;
	rootConfiguration: RootConfigurationModel | null;
	initialized: boolean;
}

@State<ConfigurationStateModel>({
	name: "configuration",
	defaults: {
		rootConfiguration: null,
		dsConfiguration: null,
		initialized: false,
	},
})
@Injectable()
export class ConfigurationState implements NgxsAfterBootstrap, NgxsOnInit {
	private resourceApiService = inject(ResourceApiService);
	private httpRequestHelper = inject(HttpRequestHelperService);

	constructor(
		private configurationService: ConfigurationService,
		private store: Store,
		private dynamicAssetInitializerService: DynamicAssetInitializerService,
	) {}

	@Selector()
	public static state(state: ConfigurationStateModel) {
		return state;
	}

	@Selector()
	public static initialized(state: ConfigurationStateModel): boolean {
		return state.initialized;
	}

	@Selector()
	public static canExtend(state: ConfigurationStateModel): boolean {
		return (
			!!state.dsConfiguration &&
			!state.dsConfiguration.dsReadOnly &&
			!state.dsConfiguration.checkinOnly
		);
	}

	@Selector()
	public static canCheckin(state: ConfigurationStateModel): boolean {
		return state.dsConfiguration?.checkin ?? false;
	}

	@Selector()
	public static resourceName(state: ConfigurationStateModel): string | undefined {
		return state.dsConfiguration?.resourceDescription;
	}

	@Selector()
	public static showDebugPage(state: ConfigurationStateModel): boolean {
		return state.dsConfiguration?.enableDebugPage ?? false;
	}

	@Selector()
	public static dsConfiguration(state: ConfigurationStateModel): DsConfigurationModel | null {
		return state.dsConfiguration;
	}

	@Selector()
	public static rootConfiguration(state: ConfigurationStateModel): RootConfigurationModel | null {
		return state.rootConfiguration;
	}

	@Selector([ConfigurationState.dsConfiguration])
	public static timeZone(dsConfig: ConfigurationStateModel["dsConfiguration"]): string {
		return dsConfig?.timeZone ?? "utc";
	}

	@Selector([ConfigurationState.dsConfiguration])
	public static calendarStart(dsConfig: ConfigurationStateModel["dsConfiguration"]): moment.Moment {
		const sod = moment().startOf("day");
		if (!dsConfig?.calendarStart) {
			return sod;
		}

		const calendarStartInZone = dsConfig.calendarStart.clone().tz(dsConfig.timeZone);
		const calendarStart = MomentHelper.applyTimeOnly(moment(), calendarStartInZone);

		return calendarStart.isAfter(sod) ? calendarStart : sod;
	}

	@Selector([ConfigurationState.dsConfiguration])
	public static calendarEnd(dsConfig: ConfigurationStateModel["dsConfiguration"]): moment.Moment {
		const eod = moment().endOf("day");
		if (!dsConfig?.calendarEnd) {
			return eod;
		}

		const calendarEndInZone = dsConfig.calendarEnd.clone().tz(dsConfig.timeZone);
		const calendarEnd = MomentHelper.applyTimeOnly(moment(), calendarEndInZone);

		return calendarEnd.isBefore(eod) ? calendarEnd : eod;
	}

	ngxsOnInit(ctx: StateContext<ConfigurationStateModel>): void {
		ctx.patchState({
			initialized: false,
		});
	}

	ngxsAfterBootstrap(ctx: StateContext<ConfigurationStateModel>) {
		const configJson = this.configurationService.config;
		if (!configJson) {
			console.error("Config not loaded...");
			return;
		}
		const state = ctx.getState();

		if (!this.isRootConfigValid(configJson.rootConfig)) {
			console.error("Root config is invalid...");
			return;
		}

		ctx.patchState({
			rootConfiguration: configJson.rootConfig,
		});

		this.store
			.dispatch(new FetchDsConfiguration(state.dsConfiguration?.resourceId, true))
			.subscribe(() => {
				this.configurationService.initializeConfigPolling();
				this.configurationService.initializeScreenRefresh();

				this.store.dispatch(new LoadBookingsOfCurrentDay());

				this.dynamicAssetInitializerService.init().then(() => {
					ctx.patchState({
						initialized: true,
					});
				});

				const currentState = ctx.getState();
				if (currentState.dsConfiguration?.timeZone) {
					moment.tz.setDefault(currentState.dsConfiguration?.timeZone);
				}
			});
	}

	@Action(FetchDsConfiguration)
	public fetchDsConfiguration(
		ctx: StateContext<ConfigurationStateModel>,
		{ resourceId, asServiceUser }: FetchDsConfiguration,
	) {
		if (!resourceId) {
			return;
		}

		this.resourceApiService.onExecuteFn = (reqOptions?: RequestOptions) =>
			this.httpRequestHelper.prepareRequest(asServiceUser, reqOptions);

		return this.resourceApiService.getDsConfig(resourceId).pipe(
			catchError((err) => {
				console.error(`DsConfig could not be loaded for resource ${resourceId}.`, err);
				return EMPTY;
			}),
			tap((config) => this.store.dispatch(new SetDsConfiguration(config))),
		);
	}

	@Action(SetDsConfiguration)
	public setDsConfiguration(
		ctx: StateContext<ConfigurationStateModel>,
		action: SetDsConfiguration,
	) {
		if (action.dsConfiguration.timeZone) {
			moment.tz.setDefault(action.dsConfiguration.timeZone);
		}

		ctx.patchState({
			dsConfiguration: action.dsConfiguration,
		});
	}

	@Action(SetRootConfiguration)
	public setRootConfiguration(
		ctx: StateContext<ConfigurationStateModel>,
		action: SetRootConfiguration,
	) {
		ctx.setState(
			patch({
				rootConfiguration: action.rootConfig,
			}),
		);
	}

	@Action(ResetDsConfiguration)
	public resetDsConfiguration(ctx: StateContext<ConfigurationStateModel>) {
		ctx.patchState({
			dsConfiguration: null,
		});

		ctx.dispatch(new SetServiceUser(null));
		return ctx.dispatch(new LoadBookingsOfCurrentDay());
	}

	private isRootConfigValid(rootConfig: RootConfigurationModel): boolean {
		if (!rootConfig) {
			return false;
		}
		return !!rootConfig.idpUrl && !!rootConfig.roomsUrl;
	}
}
